import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ViewChildren, QueryList } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MultiselectComponent } from '../primeng/multiselect/multiselect.component';
import { DateRangePickerComponent, DateRange } from '../primeng/date-range-picker/date-range-picker.component';

export type FilterFieldType = 'text' | 'select' | 'multiselect' | 'dateRange';

export interface FilterField {
  key: string;
  label: string;
  type: FilterFieldType;
  placeholder?: string;
  options?: any[]; // For select and multiselect
  value?: any; // Current value
  maxSelected?: number; // For multiselect: limit number of selections (e.g., 1 for single-select behavior)
  showClear?: boolean; // For multiselect: show clear (x) icon
  disableUncheck?: boolean; // For multiselect: prevent deselecting currently selected option
}

export interface FilterConfig {
  fields: FilterField[];
}

@Component({
  selector: 'app-filter-panel',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MultiselectComponent,
    DateRangePickerComponent
  ],
  templateUrl: './filter-panel.component.html',
  styleUrl: './filter-panel.component.scss'
})
export class FilterPanelComponent implements OnInit, OnChanges {
  @Input() fields: FilterField[] = [];
  @Input() defaultExpanded: boolean = true;
  
  @Output() filterChange = new EventEmitter<Record<string, any>>();
  @Output() clearFilters = new EventEmitter<void>();

  @ViewChildren(MultiselectComponent) multiselectComponents!: QueryList<MultiselectComponent>;
  @ViewChildren(DateRangePickerComponent) dateRangePickerComponents!: QueryList<DateRangePickerComponent>;

  showFilters = true;
  filterValues: Record<string, any> = {};
  /** Key of the field whose dropdown is currently open – raises z-index so it appears above siblings */
  openDropdownFieldKey: string | null = null;
  private lastOpenedMultiselect: MultiselectComponent | null = null;
  private lastOpenedDateRangePicker: DateRangePickerComponent | null = null;

  ngOnInit(): void {
    this.initializeFilterValues();
    // Keep filters open if there are active filters, otherwise use defaultExpanded
    const hasActiveFilters = this.activeFilterCount > 0;
    this.showFilters = hasActiveFilters || this.defaultExpanded;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fields'] && !changes['fields'].firstChange) {
      // Update filter values when fields change (e.g., options are loaded)
      this.fields.forEach(field => {
        // Parent-provided field values are the source of truth for resets/defaults.
        if (field.value !== undefined) {
          this.filterValues[field.key] = field.value;
        } else if (this.filterValues[field.key] === undefined) {
          if (field.type === 'multiselect') {
            this.filterValues[field.key] = [];
          } else if (field.type === 'dateRange') {
            this.filterValues[field.key] = null;
          } else {
            this.filterValues[field.key] = '';
          }
        }
      });
      // Keep filters open if there are active filters after fields change
      if (this.activeFilterCount > 0) {
        this.showFilters = true;
      } else if (!this.defaultExpanded) {
        this.closeAllFilterDropdowns();
        this.showFilters = false;
      }
    }
  }

  private initializeFilterValues(): void {
    // Initialize filter values from fields
    this.fields.forEach(field => {
      if (field.value !== undefined) {
        this.filterValues[field.key] = field.value;
      } else {
        // Set default values based on type
        if (field.type === 'multiselect') {
          this.filterValues[field.key] = [];
        } else if (field.type === 'dateRange') {
          this.filterValues[field.key] = null;
        } else {
          this.filterValues[field.key] = '';
        }
      }
    });
  }

  toggleFilters(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    const willCollapse = this.showFilters;
    if (willCollapse) {
      this.closeAllFilterDropdowns();
      this.blurActiveElement();
    }
    this.showFilters = !this.showFilters;
  }

  /** Close multiselect and date-range dropdowns so they don't block clicks after collapse. */
  private closeAllFilterDropdowns(): void {
    this.multiselectComponents?.forEach((ms) => {
      if (ms.isOpen) {
        ms.closeDropdown();
      }
    });
    this.dateRangePickerComponents?.forEach((picker) => {
      if (picker.isOpen) {
        picker.closeDropdown();
      }
    });
    this.lastOpenedMultiselect = null;
    this.lastOpenedDateRangePicker = null;
    this.openDropdownFieldKey = null;
  }

  /** Blur active element when collapsing so focus isn't stuck in a hidden control. */
  private blurActiveElement(): void {
    try {
      const el = document.activeElement as HTMLElement;
      if (el && typeof el.blur === 'function') {
        el.blur();
      }
    } catch (_) {}
  }

  get activeFilterCount(): number {
    let count = 0;
    this.fields.forEach(field => {
      const value = this.filterValues[field.key];
      if (field.type === 'multiselect') {
        if (value && Array.isArray(value) && value.length > 0) {
          const onlyAll =
            value.length === 1 &&
            typeof value[0] === 'object' &&
            value[0] !== null &&
            (value[0] as { value?: string }).value === 'all';
          if (!onlyAll) count++;
        }
      } else if (field.type === 'dateRange') {
        if (value && value.startDate && value.endDate) count++;
      } else if (field.type === 'text') {
        if (value && value.trim() !== '') count++;
      } else if (field.type === 'select') {
        if (
          value &&
          value !== '' &&
          value !== 'All Types' &&
          value !== 'All Activities' &&
          value !== 'All Time' &&
          value !== 'all'
        ) {
          count++;
        }
      }
    });
    return count;
  }

  onFilterChange(key: string, value: any): void {
    this.filterValues[key] = value;
    this.filterChange.emit({ ...this.filterValues });
  }

  onClearFilters(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.closeAllFilterDropdowns();
    this.resetFilterValues();
    this.clearFilters.emit();
    this.filterChange.emit({ ...this.filterValues });
  }

  /** Sync internal values from field inputs and collapse the panel when configured. */
  resetPanel(options?: { collapse?: boolean }): void {
    this.resetFilterValues();
    if (options?.collapse !== false) {
      this.closeAllFilterDropdowns();
      this.blurActiveElement();
      this.showFilters = this.defaultExpanded;
    }
  }

  private resetFilterValues(): void {
    this.fields.forEach((field) => {
      if (field.type === 'multiselect') {
        const empty: any[] = [];
        this.filterValues[field.key] = empty;
        field.value = empty;
      } else if (field.type === 'dateRange') {
        this.filterValues[field.key] = null;
        field.value = null;
      } else if (field.type === 'select') {
        this.filterValues[field.key] = '';
        field.value = '';
      } else {
        this.filterValues[field.key] = '';
        field.value = '';
      }
    });
    this.syncDropdownControlsAfterReset();
  }

  /** Reset custom dropdown UIs (multiselect / date range) so they match cleared filterValues. */
  private syncDropdownControlsAfterReset(): void {
    this.multiselectComponents?.forEach((ms) => ms.clearSelection());
    this.dateRangePickerComponents?.forEach((picker) => picker.clear());
  }

  onDateRangeChange(key: string, dateRange: DateRange): void {
    this.filterValues[key] = dateRange;
    this.filterChange.emit({ ...this.filterValues });
  }

  removeFilter(key: string): void {
    const field = this.fields.find(f => f.key === key);
    if (field) {
      if (field.type === 'multiselect') {
        this.filterValues[key] = [];
      } else if (field.type === 'dateRange') {
        this.filterValues[key] = null;
      } else if (field.type === 'select') {
        this.filterValues[key] = field.value !== undefined ? field.value : '';
      } else {
        this.filterValues[key] = '';
      }
      this.filterChange.emit({ ...this.filterValues });
    }
  }

  getFilterDisplayValue(key: string): string {
    const field = this.fields.find(f => f.key === key);
    if (!field) return '';
    
    const value = this.filterValues[key];
    if (field.type === 'multiselect') {
      return Array.isArray(value) && value.length > 0 ? value.join(', ') : '';
    } else if (field.type === 'dateRange') {
      if (value && value.startDate && value.endDate) {
        return `${this.formatDate(value.startDate)} - ${this.formatDate(value.endDate)}`;
      }
      return '';
    }
    return value || '';
  }

  private formatDate(date: Date): string {
    if (!date) return '';
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  /**
   * Determine the optionLabel for multiselect based on option type
   * If options are strings, return undefined (PrimeNG will use value directly)
   * If options are objects, return 'label' to access the label property
   */
  getOptionLabel(field: FilterField): string | undefined {
    if (!field.options || field.options.length === 0) {
      return undefined;
    }
    
    // Check if first option is an object or a string
    const firstOption = field.options[0];
    if (typeof firstOption === 'object' && firstOption !== null && 'label' in firstOption) {
      return 'label';
    }
    
    // For string arrays, return undefined so PrimeNG uses the value directly
    return undefined;
  }

  /**
   * Close all multiselect dropdowns except the one that triggered the event
   */
  onMultiselectOpen(field: FilterField): void {
    this.openDropdownFieldKey = field.key;
    // Close all date range pickers
    this.dateRangePickerComponents?.forEach(picker => {
      if (picker.isOpen) {
        picker.closeDropdown();
      }
    });
    this.lastOpenedDateRangePicker = null;
    
    // Find the multiselect that just opened (the one with isOpen=true - triggered this event)
    const currentlyOpening = this.multiselectComponents?.find(ms => ms.isOpen);
    
    // Only close lastOpenedMultiselect if it's a DIFFERENT one - never close the one that just opened
    if (this.lastOpenedMultiselect && this.lastOpenedMultiselect.isOpen && this.lastOpenedMultiselect !== currentlyOpening) {
      this.lastOpenedMultiselect.closeDropdown();
    }
    
    // Store the currently open multiselect for next time
    this.lastOpenedMultiselect = currentlyOpening || null;
  }

  onMultiselectClose(field: FilterField): void {
    if (this.openDropdownFieldKey === field.key) {
      this.openDropdownFieldKey = null;
    }
  }

  /**
   * Close all date range pickers except the one that triggered the event
   */
  onDateRangePickerOpen(field: FilterField): void {
    this.openDropdownFieldKey = field.key;
    // Close all multiselect dropdowns
    this.multiselectComponents?.forEach(multiselect => {
      if (multiselect.isOpen) {
        multiselect.closeDropdown();
      }
    });
    this.lastOpenedMultiselect = null;
    
    // Find the date range picker that just opened
    const currentlyOpening = this.dateRangePickerComponents?.find(picker => picker.isOpen);
    
    // Only close lastOpenedDateRangePicker if it's a DIFFERENT one - never close the one that just opened
    if (this.lastOpenedDateRangePicker && this.lastOpenedDateRangePicker.isOpen && this.lastOpenedDateRangePicker !== currentlyOpening) {
      this.lastOpenedDateRangePicker.closeDropdown();
    }
    
    // Store the currently open date range picker for next time
    this.lastOpenedDateRangePicker = currentlyOpening || null;
  }

  onDateRangePickerClose(field: FilterField): void {
    if (this.openDropdownFieldKey === field.key) {
      this.openDropdownFieldKey = null;
    }
  }
}
